import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixAchievementsInstructorsV2InstructorsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/instructors-service',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/instructors-service',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/instructors-service',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/instructors-service',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/instructors-service',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/instructors-service',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/instructors-service',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/instructors-service',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/_api/instructors-service',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_online-programs_instructors';

/** Creates a new instructor */
export function createInstructor(payload: object): RequestOptionsFactory<any> {
  function __createInstructor({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'instructor.createdDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.achievements.instructors.v2.instructor',
      method: 'POST' as any,
      methodFqn:
        'wix.achievements.instructors.v2.InstructorsService.CreateInstructor',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAchievementsInstructorsV2InstructorsServiceUrl({
        protoPath: '/v2/instructors',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'instructor.createdDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __createInstructor;
}

/** Invite instructor by provided email */
export function inviteInstructor(payload: object): RequestOptionsFactory<any> {
  function __inviteInstructor({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.achievements.instructors.v2.instructor',
      method: 'POST' as any,
      methodFqn:
        'wix.achievements.instructors.v2.InstructorsService.InviteInstructor',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAchievementsInstructorsV2InstructorsServiceUrl({
        protoPath: '/v2/instructors/invite',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __inviteInstructor;
}

/** Lists instructors created on the site */
export function listInstructors(payload: object): RequestOptionsFactory<any> {
  function __listInstructors({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.achievements.instructors.v2.instructor',
      method: 'POST' as any,
      methodFqn:
        'wix.achievements.instructors.v2.InstructorsService.ListInstructors',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAchievementsInstructorsV2InstructorsServiceUrl({
        protoPath: '/v2/instructors/list',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'instructors.createdDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __listInstructors;
}

/** Query instructors */
export function queryInstructors(payload: object): RequestOptionsFactory<any> {
  function __queryInstructors({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.achievements.instructors.v2.instructor',
      method: 'POST' as any,
      methodFqn:
        'wix.achievements.instructors.v2.InstructorsService.QueryInstructors',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAchievementsInstructorsV2InstructorsServiceUrl({
        protoPath: '/v2/instructors/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'instructors.createdDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __queryInstructors;
}

/** Updates an instructor, supports partial update */
export function updateInstructor(payload: object): RequestOptionsFactory<any> {
  function __updateInstructor({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'instructor.createdDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.achievements.instructors.v2.instructor',
      method: 'PATCH' as any,
      methodFqn:
        'wix.achievements.instructors.v2.InstructorsService.UpdateInstructor',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAchievementsInstructorsV2InstructorsServiceUrl({
        protoPath: '/v2/instructors/{instructor.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'instructor.createdDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __updateInstructor;
}

/** Deletes an instructor */
export function deleteInstructor(payload: object): RequestOptionsFactory<any> {
  function __deleteInstructor({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.achievements.instructors.v2.instructor',
      method: 'DELETE' as any,
      methodFqn:
        'wix.achievements.instructors.v2.InstructorsService.DeleteInstructor',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAchievementsInstructorsV2InstructorsServiceUrl({
        protoPath: '/v2/instructors/{instructorId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'instructor.createdDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __deleteInstructor;
}

/** Assigns an instructor to the program */
export function assignInstructorToProgram(
  payload: object
): RequestOptionsFactory<any> {
  function __assignInstructorToProgram({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.achievements.instructors.v2.instructor',
      method: 'POST' as any,
      methodFqn:
        'wix.achievements.instructors.v2.InstructorsService.AssignInstructorToProgram',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAchievementsInstructorsV2InstructorsServiceUrl({
        protoPath: '/v2/instructors/{instructorId}/assign',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __assignInstructorToProgram;
}

/** Unassigns an instructor from the program */
export function unassignInstructorFromProgram(
  payload: object
): RequestOptionsFactory<any> {
  function __unassignInstructorFromProgram({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.achievements.instructors.v2.instructor',
      method: 'POST' as any,
      methodFqn:
        'wix.achievements.instructors.v2.InstructorsService.UnassignInstructorFromProgram',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAchievementsInstructorsV2InstructorsServiceUrl({
        protoPath: '/v2/instructors/{instructorId}/unassign',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __unassignInstructorFromProgram;
}

/** Assigns or unassigns instructors to the program */
export function changeProgramInstructors(
  payload: object
): RequestOptionsFactory<any> {
  function __changeProgramInstructors({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.achievements.instructors.v2.instructor',
      method: 'POST' as any,
      methodFqn:
        'wix.achievements.instructors.v2.InstructorsService.ChangeProgramInstructors',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAchievementsInstructorsV2InstructorsServiceUrl({
        protoPath: '/v2/assignments',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __changeProgramInstructors;
}
